import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Star } from "lucide-react";
import Layout from "@/components/Layout";
import Hero from "@/components/Hero";
import LaunchOffer from "@/components/LaunchOffer";
import TrustBadges from "@/components/TrustBadges";
import { api } from "@/lib/api";
import { BOOK_URL } from "@/lib/booking";

function Marquee() {
  const items = ["END OF TENANCY", "DEEP CLEAN", "HANDYMAN REPAIRS", "MOVE-IN READY", "CARPET CLEAN", "GARDEN TIDY", "PAINT & DECORATE"];
  const doubled = [...items, ...items];
  return (
    <div className="overflow-hidden border-y-2 border-[#0B1215] bg-[#0B1215] text-[#F8F9FA]">
      <div className="marquee-track flex whitespace-nowrap py-5">
        {doubled.map((t, i) => (
          <span key={`${t}-${i}`} className="font-display text-2xl tracking-tighter px-10 flex items-center gap-10">
            {t} <span className="text-[#FF5A36]">✱</span>
          </span>
        ))}
      </div>
    </div>
  );
}

const EXPLORE = [
  {
    to: "/services",
    eyebrow: "01 · WHAT I DO",
    title: "Services",
    blurb: "Deep cleans, repairs, gardens & carpets — all from one trusted pair of hands.",
  },
  {
    to: "/work",
    eyebrow: "02 · SEE THE WORK",
    title: "Before / After",
    blurb: "Real properties, real results. Slide the drag bar and see the transformation.",
  },
  {
    to: "/pricing",
    eyebrow: "03 · INVESTMENT GUIDE",
    title: "Pricing",
    blurb: "Honest starting prices for every service. Fixed quotes in 24 hours.",
  },
  {
    to: "/about",
    eyebrow: "04 · WHO I AM",
    title: "About",
    blurb: "Qualified handyman, years of property experience, locally rooted.",
  },
];

export default function Home() {
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/testimonials").then((r) => setTestimonials(r.data.slice(0, 2))).catch(() => {});
  }, []);

  return (
    <Layout>
      <Hero />
      <LaunchOffer />
      <TrustBadges />
      <Marquee />

      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="font-mono-label text-[#FF5A36] mb-4">· EXPLORE</div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#0B1215] tracking-tighter leading-[0.95] max-w-2xl">
                Find what you<br />
                <span className="italic font-light">need fast.</span>
              </h2>
            </div>
            <p className="text-[#495057] max-w-sm leading-relaxed">
              Each part of the site has its own page now — pick a starting point.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {EXPLORE.map((e, i) => (
              <motion.div
                key={e.to}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
              >
                <Link
                  to={e.to}
                  className="group block border-2 border-[#0B1215] p-8 lg:p-10 bg-[#F8F9FA] hover:bg-[#0B1215] hover:text-[#F8F9FA] transition-colors"
                  data-testid={`home-explore-${e.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="font-mono-label text-[#FF5A36] mb-4">· {e.eyebrow}</div>
                  <div className="flex items-end justify-between gap-4">
                    <h3 className="font-display text-3xl sm:text-4xl tracking-tighter leading-tight">{e.title}</h3>
                    <ArrowRight size={28} className="group-hover:translate-x-1.5 transition-transform shrink-0 mb-1" />
                  </div>
                  <p className="text-sm mt-4 opacity-75 max-w-md">{e.blurb}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {testimonials.length > 0 && (
        <section className="py-20 sm:py-24 bg-[#E9ECEF] border-y border-[#0B1215]/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="font-mono-label text-[#FF5A36] mb-8">· WHAT CLIENTS SAY</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {testimonials.map((t) => (
                <blockquote key={t.id} className="bg-[#F8F9FA] border-2 border-[#0B1215] p-8 flex flex-col gap-4">
                  <p className="font-display text-xl sm:text-2xl leading-snug tracking-tight text-[#0B1215]">
                    "{t.quote}"
                  </p>
                  <footer className="text-sm">
                    <span className="font-semibold text-[#0B1215]">{t.name}</span>
                    <span className="font-mono-label text-[#495057] ml-3">· {t.location}</span>
                  </footer>
                </blockquote>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-24 sm:py-36 bg-[#F8F9FA] relative overflow-hidden" data-testid="home-featured-review">
        <div aria-hidden className="pointer-events-none absolute -top-24 -left-16 font-display text-[#FF5A36]/10 text-[28rem] leading-none select-none">{'"'}</div>
        <div className="max-w-6xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="font-mono-label text-[#FF5A36] mb-6 flex items-center gap-2">
            <Star size={14} className="fill-[#FF5A36] text-[#FF5A36]" /> · FEATURED REVIEW
          </div>
          <div className="flex items-center gap-1 mb-8" aria-label="5 out of 5 stars">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={20} className="fill-[#FF5A36] text-[#FF5A36]" />
            ))}
          </div>
          <motion.blockquote
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="font-display text-[#0B1215] tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-7xl xl:text-8xl"
          >
            <span aria-hidden className="text-[#FF5A36]">{'"'}</span>
            We would <span className="italic font-light">100% recommend</span> him for any of his services.
            <span aria-hidden className="text-[#FF5A36]">{'"'}</span>
          </motion.blockquote>
          <div className="mt-12 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 pt-8 border-t-2 border-[#0B1215]">
            <div>
              <div className="font-display text-2xl text-[#0B1215] leading-tight">Abigail Farmer</div>
              <div className="font-mono-label text-[#495057] mt-1">· Google Review · 5★ · Warwickshire</div>
            </div>
            <Link
              to="/reviews"
              className="group inline-flex items-center gap-2 font-mono-label text-[#0B1215] hover:text-[#FF5A36] transition-colors"
              data-testid="home-featured-review-cta"
            >
              Read all reviews
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-24 bg-[#0B1215] text-[#F8F9FA] grain relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 text-center">
          <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.92]">
            Ready when <span className="italic font-light text-[#FF5A36]">you are.</span>
          </h2>
          <p className="mt-6 text-[#F8F9FA]/70 max-w-xl mx-auto leading-relaxed">
            Live availability on my diary — pick your slot, get instant confirmation. No agency markup, no pushy sales.
          </p>
          <a
            href={BOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-10 inline-flex items-center gap-2 bg-[#FF5A36] hover:bg-[#E04928] text-[#F8F9FA] px-8 py-4 font-mono-label active:scale-95 transition-all"
            data-testid="home-final-cta"
          >
            Book a slot <ArrowRight size={16} />
          </a>
        </div>
      </section>
    </Layout>
  );
}
